/**
 * LRU (Least Recently Used) Cache Implementation
 * 
 * A memory-efficient cache with automatic eviction of least recently used items.
 * Prevents memory leaks by enforcing maximum cache size.
 * 
 * Features:
 * - O(1) get, set, and delete operations
 * - Automatic eviction when max size is reached
 * - TTL (Time To Live) support for entries
 * - Memory usage tracking
 * - Statistics for monitoring cache performance
 * 
 * @example
 * const cache = new LRUCache<User>({ maxSize: 100, ttl: 5 * 60 * 1000 });
 * cache.set('user-123', userData);
 * const user = cache.get('user-123'); // Returns user or null if expired/evicted
 */

export interface LRUCacheOptions {
  maxSize: number;           // Maximum number of items to store
  ttl?: number;             // Time to live in milliseconds (optional)
  onEvict?: (key: string, value: unknown) => void; // Callback when item is evicted
}

interface CacheEntry<T> {
  value: T;
  timestamp: number;
  accessCount: number;
}

export class LRUCache<T> {
  private cache: Map<string, CacheEntry<T>>;
  private readonly maxSize: number;
  private readonly ttl?: number;
  private readonly onEvict?: (key: string, value: T) => void;
  
  // Statistics
  private hits = 0;
  private misses = 0;
  private evictions = 0;

  constructor(options: LRUCacheOptions) {
    this.cache = new Map();
    this.maxSize = options.maxSize;
    this.ttl = options.ttl;
    this.onEvict = options.onEvict as ((key: string, value: T) => void) | undefined;
  }

  /**
   * Get a value from the cache
   * Returns null if not found or expired
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.misses++;
      return null;
    }

    // Check if entry has expired
    if (this.ttl && Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      this.misses++;
      return null;
    }

    // Update access count and move to end (most recently used)
    entry.accessCount++;
    this.cache.delete(key);
    this.cache.set(key, entry);
    
    this.hits++;
    return entry.value;
  }

  /**
   * Set a value in the cache
   * Automatically evicts LRU item if cache is full
   */
  set(key: string, value: T): void {
    // If key already exists, update it
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Evict oldest item if cache is full
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    // Add new entry
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      accessCount: 0
    });
  }

  /**
   * Check if a key exists in the cache (without updating access)
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }

    // Check if expired
    if (this.ttl && Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete a specific key from the cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all entries from the cache
   */
  clear(): void {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
    this.evictions = 0;
  }

  /**
   * Get current cache size
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? (this.hits / total) * 100 : 0;

    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hits: this.hits,
      misses: this.misses,
      evictions: this.evictions,
      hitRate: hitRate.toFixed(2) + '%',
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.hits = 0;
    this.misses = 0;
    this.evictions = 0;
  }

  /**
   * Get all keys in the cache (for debugging)
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Cleanup expired entries
   * Call this periodically to free memory
   */
  cleanup(): number {
    if (!this.ttl) return 0;

    const now = Date.now();
    let cleaned = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    return cleaned;
  }

  /**
   * Evict the least recently used item
   */
  private evictOldest(): void {
    // The first item in the Map is the oldest (LRU)
    const oldestKey = this.cache.keys().next().value;
    
    if (oldestKey) {
      const entry = this.cache.get(oldestKey);
      if (entry) {
        this.onEvict?.(oldestKey, entry.value);
      }
      this.cache.delete(oldestKey);
      this.evictions++;
    }
  }

  /**
   * Estimate memory usage in bytes (rough approximation)
   */
  private estimateMemoryUsage(): string {
    // Rough estimate: each entry ~1KB on average
    const bytes = this.cache.size * 1024;
    
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  }
}

/**
 * Create a versioned LRU cache with localStorage persistence
 * Automatically clears cache if version changes
 */
export function createVersionedLRUCache<T>(
  name: string,
  version: string,
  options: LRUCacheOptions
): LRUCache<T> {
  if (typeof window !== 'undefined') {
    const storageKey = `${name}_cache_version`;
    const storedVersion = localStorage.getItem(storageKey);
    
    if (storedVersion !== version) {
      localStorage.setItem(storageKey, version);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${name}] Cache version changed (${storedVersion} → ${version}), clearing cache`);
      }
    }
  }

  return new LRUCache<T>(options);
}

/**
 * Adaptive cache with dynamic TTL based on access patterns
 */
export class AdaptiveLRUCache<T> extends LRUCache<T> {
  private accessPatterns = new Map<string, number[]>();
  private readonly adaptiveWindow = 10; // Track last N accesses

  get(key: string): T | null {
    const value = super.get(key);
    
    if (value !== null) {
      this.recordAccess(key);
    }
    
    return value;
  }

  /**
   * Get recommended TTL for a key based on access patterns
   */
  getRecommendedTTL(key: string): number | undefined {
    const accesses = this.accessPatterns.get(key);
    
    if (!accesses || accesses.length < 2) {
      return undefined;
    }

    // Calculate average time between accesses
    let totalInterval = 0;
    for (let i = 1; i < accesses.length; i++) {
      totalInterval += accesses[i] - accesses[i - 1];
    }
    
    const avgInterval = totalInterval / (accesses.length - 1);
    
    // Set TTL to 2x the average interval (with min/max bounds)
    const recommendedTTL = Math.min(
      Math.max(avgInterval * 2, 60 * 1000), // Min 1 minute
      60 * 60 * 1000 // Max 1 hour
    );

    return recommendedTTL;
  }

  private recordAccess(key: string): void {
    const now = Date.now();
    const accesses = this.accessPatterns.get(key) || [];
    
    accesses.push(now);
    
    // Keep only last N accesses
    if (accesses.length > this.adaptiveWindow) {
      accesses.shift();
    }
    
    this.accessPatterns.set(key, accesses);
  }

  clear(): void {
    super.clear();
    this.accessPatterns.clear();
  }
}


